// LearnCore Frontend - 文言文材料列表页面

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import RecitationModal from '../components/RecitationModal';
import type { ClassicalPassage, ClassicalProcessingStatus, Tag } from '../types';
import {
  PlusIcon,
  BookOpenIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ShareIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

export default function ClassicalChineseListPage() {
  const [searchParams] = useSearchParams();
  const [passages, setPassages] = useState<ClassicalPassage[]>([]);
  const [filteredPassages, setFilteredPassages] = useState<ClassicalPassage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'public' | 'private'>('all');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedGradeTag, setSelectedGradeTag] = useState<string>('all');
  const [gradeTags, setGradeTags] = useState<Tag[]>([]);
  
  // 背诵相关状态
  const [recitationModal, setRecitationModal] = useState<{
    isOpen: boolean;
    passageId: string;
    title: string;
    content: string;
  }>({
    isOpen: false,
    passageId: '',
    title: '',
    content: ''
  });

  // 初始化时显示URL参数中的消息
  useEffect(() => {
    const message = searchParams.get('message');
    if (message) {
      setSuccessMessage(message);
      // 3秒后自动清除消息
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [searchParams]);

  useEffect(() => {
    loadPassages();
    loadGradeTags();
  }, []);

  // 轮询机制：如果有处理中的材料，每10秒刷新一次
  useEffect(() => {
    const hasProcessing = passages.some(p => 
      p.processing_status === 'pending' || p.processing_status === 'processing'
    );

    if (hasProcessing) {
      const interval = setInterval(() => {
        loadPassages();
      }, 10000); // 每10秒刷新一次

      return () => clearInterval(interval);
    }
  }, [passages]);

  useEffect(() => {
    // 根据搜索查询和筛选类型过滤文言文材料
    let filtered = passages;

    if (filterType === 'public') {
      filtered = filtered.filter(p => p.is_public === 1);
    } else if (filterType === 'private') {
      filtered = filtered.filter(p => p.is_public === 0);
    }

    // 年级标签筛选
    if (selectedGradeTag !== 'all') {
      filtered = filtered.filter(p =>
        p.tags && p.tags.some(tag => tag.id === selectedGradeTag && tag.type === 'grade')
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(query) ||
        p.original_text.toLowerCase().includes(query)
      );
    }

    setFilteredPassages(filtered);
  }, [searchQuery, filterType, selectedGradeTag, passages]);

  const loadPassages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getClassicalPassages();
      setPassages(response.passages);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取文言文材料失败');
    } finally {
      setLoading(false);
    }
  };

  const loadGradeTags = async () => {
    try {
      const response = await apiClient.getTags('grade');
      setGradeTags(response.tags);
    } catch (err: any) {
      console.error('获取年级标签失败:', err);
    }
  };

  const handleDelete = async (passageId: string) => {
    if (!confirm('确定要删除这个文言文材料吗？此操作不可撤销。')) {
      return;
    }

    try {
      setDeletingId(passageId);
      await apiClient.deleteClassicalPassage(passageId);
      setPassages(passages.filter(p => p.id !== passageId));
    } catch (err: any) {
      setError(err.response?.data?.error || '删除文言文材料失败');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRetry = async (passageId: string) => {
    try {
      setRetryingId(passageId);
      await apiClient.triggerClassicalExtraction(passageId);
      // 重新加载数据
      await loadPassages();
    } catch (err: any) {
      setError(err.response?.data?.error || '重新提取失败');
    } finally {
      setRetryingId(null);
    }
  };

  // 开始背诵
  const handleStartRecitation = (passage: ClassicalPassage) => {
    setRecitationModal({
      isOpen: true,
      passageId: passage.id,
      title: passage.title,
      content: passage.original_text
    });
  };

  // 关闭背诵模态框
  const handleCloseRecitation = () => {
    setRecitationModal({
      isOpen: false,
      passageId: '',
      title: '',
      content: ''
    });
  };

  const getStatusIcon = (status: ClassicalProcessingStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'partial':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: ClassicalProcessingStatus) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'failed':
        return '提取失败';
      case 'processing':
        return '处理中';
      case 'pending':
        return '等待处理';
      case 'partial':
        return '部分完成';
      default:
        return '草稿';
    }
  };

  const getStatusColor = (status: ClassicalProcessingStatus) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'processing':
        return 'text-blue-600 bg-blue-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'partial':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray- dark:bg-gray-50';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <BookOpenIcon className="h-8 w-8 mr-3 text-amber-600" />
              文言文学习
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              管理和学习文言文材料，智能提取知识点
            </p>
          </div>
          <Link
            to="/classical-chinese/create"
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md flex items-center transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            创建文言文材料
          </Link>
        </div>
      </div>

      {/* 成功消息 */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="btn-outline text-sm"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 搜索和筛选 */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="搜索文言文材料..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              全部
            </button>
            <button
              onClick={() => setFilterType('public')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === 'public'
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              公开
            </button>
            <button
              onClick={() => setFilterType('private')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === 'private'
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              私有
            </button>
          </div>
              </div>
      </div>

      {/* 年级标签筛选 */}
      {gradeTags.length > 0 && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">年级：</span>
            <button
              onClick={() => setSelectedGradeTag('all')}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                selectedGradeTag === 'all'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-medium'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
              }`}
            >
              全部
            </button>
            {gradeTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => setSelectedGradeTag(tag.id)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedGradeTag === tag.id
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 文言文材料列表 */}
      {filteredPassages.length === 0 ? (
        <div className="text-center py-12">
          <BookOpenIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery || filterType !== 'all' ? '没有找到匹配的文言文材料' : '还没有文言文材料'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || filterType !== 'all' 
              ? '尝试调整搜索条件或筛选条件'
              : '开始创建您的第一个文言文学习材料'
            }
          </p>
          {!searchQuery && filterType === 'all' && (
            <Link
              to="/classical-chinese/create"
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              创建文言文材料
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredPassages.map((passage) => (
            <div 
              key={passage.id} 
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
            >
              <div className="p-5 flex-1 flex flex-col">
                {/* 创建日期 + 状态 + 公开/私人状态 - 置顶 */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                  <span className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {formatDate(passage.updated_at)}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    {/* 只显示非完成状态 */}
                    {passage.processing_status !== 'completed' && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(passage.processing_status)}`}>
                        {getStatusIcon(passage.processing_status)}
                        <span className="ml-1">{getStatusText(passage.processing_status)}</span>
                      </span>
                    )}

                    {passage.is_public === 1 && (
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                        <ShareIcon className="h-3 w-3 mr-1 inline" />
                        公开
                      </span>
                    )}
                  </div>
                </div>

                {/* 标题 */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                  {passage.title}
                </h3>

                {/* 原文预览 - 固定高度区域 */}
                <div className="mb-3 min-h-[3rem]">
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3 font-serif">
                    {passage.original_text.length > 150
                      ? passage.original_text.substring(0, 150) + '...'
                      : passage.original_text
                    }
                  </p>
                </div>

                {/* 标签展示 - 固定高度区域 */}
                <div className="mb-4 min-h-[1.5rem]">
                  {passage.tags && passage.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {/* 只显示年级标签 */}
                      {passage.tags.filter(tag => tag.type === 'grade').map(tag => (
                        <span key={tag.id} className="px-2 py-0.5 rounded-full text-[11px] bg-blue-100 text-blue-700 border border-blue-200 font-medium">
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-xs italic">暂无年级标签</div>
                  )}
                </div>

                {/* 错误信息 */}
                {passage.processing_error && (
                  <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs text-red-700">
                      <strong>错误：</strong>{passage.processing_error.length > 50 
                        ? passage.processing_error.substring(0, 50) + '...'
                        : passage.processing_error
                      }
                    </p>
                  </div>
                )}

                {/* 操作按钮 - 置底 */}
                <div className="flex space-x-2 mt-auto">
                  <Link
                    to={`/classical-chinese/${passage.id}`}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-center text-sm py-2 rounded-md transition-colors"
                  >
                    学习
                  </Link>
                  
                  <button
                    onClick={() => handleStartRecitation(passage)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-center text-sm py-2 rounded-md transition-colors flex items-center justify-center"
                    title="开始背诵"
                  >
                    <AcademicCapIcon className="h-4 w-4 mr-1" />
                    背诵
                  </button>
                  
                  <Link
                    to={`/classical-chinese/${passage.id}`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="查看详情"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Link>
                  
                  <Link
                    to={`/classical-chinese/${passage.id}/edit`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="编辑"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Link>
                  
                  {(passage.processing_status === 'failed' || passage.processing_status === 'partial') && (
                    <button
                      onClick={() => handleRetry(passage.id)}
                      disabled={retryingId === passage.id}
                      className="btn-outline px-3 py-2 text-sm text-green-600 hover:text-green-700 hover:border-green-300 disabled:opacity-50"
                      title={passage.processing_status === 'partial' ? '重新提取失败段落' : '重新提取'}
                    >
                      {retryingId === passage.id ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowPathIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDelete(passage.id)}
                    disabled={deletingId === passage.id}
                    className="btn-outline px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
                    title="删除"
                  >
                    {deletingId === passage.id ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 背诵模态框 */}
      <RecitationModal
        isOpen={recitationModal.isOpen}
        onClose={handleCloseRecitation}
        title={recitationModal.title}
        content={recitationModal.content}
        passageId={recitationModal.passageId}
      />
    </div>
  );
}
